/* istanbul ignore file */
/**
 * Hosted iframe loader for the custom lead form (Option 2).
 *
 * Lets a host page embed the form with a container div and one script tag,
 * instead of a hand-written iframe plus inline glue. Everything that would
 * otherwise be copy-pasted into each page lives here, so it can be changed
 * once and redeployed:
 *
 *   1. builds the iframe (the form URL is derived from THIS script's own origin,
 *      so the staging script embeds the staging form with no config),
 *   2. passes the host page's GA4 client id in as `?ga_cid=…` — the form is on
 *      its own domain and can't read the host's `_ga` cookie, so without this
 *      GA4 counts every lead as a brand-new user with no campaign attribution,
 *   3. passes the host page's Google Ads click id in as `?gclid=…` (from the
 *      landing URL or the `_gcl_aw` cookie), so the server-side conversion
 *      event can match to its ad click without needing a website tag,
 *   4. auto-sizes the iframe from the form's `mr-lead-form-height` messages,
 *   5. passes the host page's `?lead_channel=…` through, so a landing page for
 *      a known traffic source can preset "how did you hear about us?".
 *
 * Usage:
 *   <div data-mr-lead-form-frame></div>
 *   <script src="https://lead-form.maxretail.app/iframe.js" defer></script>
 *
 * Optional attributes on the container:
 *   data-src           — override the form URL (defaults to this script's origin)
 *   data-title         — iframe title for screen readers
 *   data-scroll-offset — px to leave above the frame when scrolling to it
 *                        (defaults to the measured sticky-header height)
 */
import { withAttribution } from './attribution.js'
import { LEAD_CHANNEL_PARAM } from './submission.js'

const SCRIPT_SRC = (document.currentScript as HTMLScriptElement | null)?.src

const formUrl = (container: HTMLElement): string => {
  const override = container.dataset.src
  if (override) {
    return override
  }
  // Same origin as this script: /iframe.js → / (the hosted form page).
  return SCRIPT_SRC ? new URL('.', SCRIPT_SRC).href : '/'
}

/** Breathing room between the host's header and the top of the frame. */
const SCROLL_GAP = 16

/** Probe this far into the frame, so we sample the frame and not its border. */
const PROBE_INSET = 2

/** Ignore an "overlay" taller than this share of the viewport — see below. */
const MAX_OVERLAY = 0.4

/**
 * Height of the host's banner landmark, whatever its positioning. Divi (and most
 * WordPress themes, Webflow, Squarespace) mark the site header up as `<header>`
 * or `role="banner"`, so this is a good guess even for a header that isn't
 * `fixed`/`sticky` right now and so is invisible to `pinnedOverlayBottom`.
 *
 * Counted only when it's lying across the top of the viewport at the moment we
 * measure, which is what makes it an obstruction — no matter what CSS (or host
 * JS) puts it there. Also filtered to something header-shaped: most of the
 * viewport wide, and not deep enough to be a page section, since `header` is
 * legal inside an `<article>` too.
 *
 * Returns its distance from the viewport top, matching `pinnedOverlayBottom`.
 */
const bannerHeight = (): number => {
  let best = 0
  for (const el of document.querySelectorAll<HTMLElement>(
    'header, [role="banner"]'
  )) {
    const rect = el.getBoundingClientRect()
    const acrossTop = rect.top <= PROBE_INSET && rect.bottom > PROBE_INSET
    if (
      acrossTop &&
      rect.width > window.innerWidth * 0.6 &&
      rect.bottom <= window.innerHeight * MAX_OVERLAY
    ) {
      best = Math.max(best, rect.bottom)
    }
  }
  return best
}

/** Bottom of the lowest pinned element found at viewport row `y`. */
const pinnedBottomAt = (y: number): number => {
  const seen = new Set<Element>()
  let bottom = 0
  for (const fraction of [0.1, 0.5, 0.9]) {
    for (const hit of document.elementsFromPoint(
      window.innerWidth * fraction,
      y
    )) {
      for (let el: Element | null = hit; el; el = el.parentElement) {
        if (seen.has(el)) {
          break
        }
        seen.add(el)
        const { position } = window.getComputedStyle(el)
        if (position === 'fixed' || position === 'sticky') {
          bottom = Math.max(bottom, el.getBoundingClientRect().bottom)
        }
      }
    }
  }
  return bottom
}

/**
 * Bottom of everything the host pins across the top of the viewport, walking down
 * through *stacked* bars rather than sampling a single row.
 *
 * Sampling only `y = 1` finds the topmost bar and stops. On this site that meant
 * finding WordPress's 32px admin toolbar and never the site nav sitting directly
 * beneath it — so the scroll aimed ~100px short and the correction pass had to
 * clean up, which is the second scroll. Each round probes just below what the last
 * one found, so N bars resolve to the bottom of the lowest.
 */
const pinnedOverlayBottom = (): number => {
  const maxBars = 4
  const ceiling = window.innerHeight * MAX_OVERLAY
  let bottom = 0
  for (let i = 0; i < maxBars; i += 1) {
    const y = bottom + 1
    if (y > ceiling) {
      break
    }
    const next = pinnedBottomAt(y)
    // Nothing new below the last bar: the stack ends here.
    if (next <= bottom) {
      break
    }
    bottom = next
  }
  // A full-screen fixed overlay (cookie wall, modal backdrop) is not a header;
  // obeying it would push the form off-screen entirely.
  return bottom > ceiling ? 0 : bottom
}

/**
 * How many px of the frame's top edge are covered by something the host paints
 * over it, measured directly rather than inferred from a header's geometry.
 *
 * Hit-test a few points just inside the frame's top edge: `elementsFromPoint`
 * returns them topmost-first, so anything listed *before* our frame is painted
 * on top of it. That catches an absolutely-positioned overlay nav, a header that
 * animates in as you scroll up, and one that shrinks when it sticks — all cases
 * where measuring "the sticky header" up front gets the wrong number, which is
 * what left the outcome screen's heading tucked behind the nav.
 *
 * A caveat worth knowing: `pointer-events: none` elements are invisible to
 * hit-testing, so an overlay using it can't be detected this way. Hosts don't
 * generally do that to a nav (its links have to be clickable).
 */
const overlapAtFrameTop = (frame: HTMLElement): number => {
  const rect = frame.getBoundingClientRect()
  const y = rect.top + PROBE_INSET
  // Frame top above the viewport: not an overlap problem, and the probe would
  // sample whatever sits at the top of the page instead.
  if (y < 0) {
    return 0
  }
  let cover = 0
  for (const fraction of [0.15, 0.5, 0.85]) {
    for (const el of document.elementsFromPoint(
      rect.left + rect.width * fraction,
      y
    )) {
      // Reached our own frame (or an ancestor of it): nothing above it here.
      if (el === frame || el.contains(frame)) {
        break
      }
      cover = Math.max(cover, el.getBoundingClientRect().bottom - rect.top)
    }
  }
  // A full-screen overlay (cookie wall, modal backdrop) covers the frame no
  // matter where we scroll; obeying it would push the form off-screen entirely.
  if (cover > window.innerHeight * MAX_OVERLAY) {
    return 0
  }
  return Math.max(cover, 0)
}

/**
 * Run `fn` once the page has actually *arrived* at `target` and stopped moving.
 *
 * Stillness alone is not enough, and assuming otherwise is what made this stutter
 * for real: a smooth scroll eases out, so `scrollY` can repeat the same value for
 * several frames while the animation is still running. Probing then reports the
 * frame as covered — it hasn't finished travelling — and the "correction" fires
 * into an in-flight scroll, which reads as a second scroll after a pause.
 *
 * `scrollend` would answer this directly but isn't in every browser we support,
 * and wouldn't wait out a header that transitions in afterwards.
 */
const afterArrival = (target: number, fn: () => void): void => {
  const stillFrames = 4
  const maxFrames = 240
  let last = window.scrollY
  let still = 0
  let frames = 0
  const tick = (): void => {
    frames += 1
    const y = window.scrollY
    // Treat either end of the scroll range as arrival: the browser clamps there,
    // so a target beyond it is never reached and we would spin until timeout.
    const limit = Math.max(
      (document.scrollingElement?.scrollHeight ?? 0) - window.innerHeight,
      0
    )
    const arrived = Math.abs(y - target) <= 1 || y <= 0 || y >= limit - 1
    still = arrived && Math.abs(y - last) < 1 ? still + 1 : 0
    last = y
    if ((arrived && still >= stillFrames) || frames >= maxFrames) {
      fn()
      return
    }
    requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}

/**
 * Scroll the frame's top edge into view, clear of the host's header.
 *
 * Aim using the header we can measure now, then — only once that scroll has
 * genuinely finished, so the host's scroll-driven header changes have played out —
 * check whether the top edge really is visible and nudge down if it isn't. In the
 * normal case the aim is right and the check is silent.
 */
const scrollToFrame = (frame: HTMLElement, override?: string): void => {
  /** Put the frame's top `offset + SCROLL_GAP` below the viewport top. */
  const aim = (offset: number): number => {
    const top =
      frame.getBoundingClientRect().top + window.scrollY - offset - SCROLL_GAP
    const target = Math.max(top, 0)
    window.scrollTo({ top: target, behavior: 'smooth' })
    return target
  }

  const explicit = Number(override)
  if (override && Number.isFinite(explicit)) {
    aim(explicit)
    return
  }

  // Aim with the most generous estimate the two measurements give us. Landing a
  // little low is invisible; landing high needs a correction, which the user sees
  // as a second scroll after a pause.
  const first = aim(Math.max(pinnedOverlayBottom(), bannerHeight()))

  // `cover` is measured down from the frame's top edge, so correcting it means
  // moving the frame down by that much (plus the gap) — a relative nudge, not
  // the absolute aim above, which is anchored to the same edge we're fixing.
  let passes = 0
  const correct = (): void => {
    const cover = overlapAtFrameTop(frame)
    if (cover <= PROBE_INSET) {
      return
    }
    const target = Math.max(window.scrollY - (cover + SCROLL_GAP), 0)
    window.scrollTo({ top: target, behavior: 'smooth' })
    // One round is normally enough; a second covers a header that only finishes
    // moving in response to the corrective scroll itself.
    passes += 1
    if (passes < 2) {
      afterArrival(target, correct)
    }
  }
  afterArrival(first, correct)
}

const mount = (container: HTMLElement): void => {
  // Resolved against the host page, so a relative `data-src` ("./", "/form/")
  // works — without a base, `new URL` throws on anything relative and the
  // widget silently never mounts.
  const url = withAttribution(new URL(formUrl(container), window.location.href))
  // Forwarded raw: the form validates it against the HubSpot options and
  // ignores anything it doesn't recognise.
  const leadChannel = new URLSearchParams(window.location.search).get(
    LEAD_CHANNEL_PARAM
  )
  if (leadChannel) {
    url.searchParams.set(LEAD_CHANNEL_PARAM, leadChannel)
  }

  const frame = document.createElement('iframe')
  frame.src = url.href
  frame.title = container.dataset.title ?? 'Get started with Max Retail'
  frame.loading = 'lazy'
  frame.style.width = '100%'
  frame.style.border = '0'
  frame.style.display = 'block'
  // Provisional; the form reports its real height as soon as it renders.
  frame.style.height = '900px'
  frame.setAttribute('scrolling', 'no')
  container.replaceChildren(frame)

  // Auto-height + scroll requests. Origin-checked so an unrelated frame can't
  // resize or scroll ours.
  window.addEventListener('message', (event: MessageEvent) => {
    if (event.origin !== url.origin || event.source !== frame.contentWindow) {
      return
    }
    const data = event.data as
      | { type?: string; height?: number }
      | null
      | undefined
    if (
      data?.type === 'mr-lead-form-height' &&
      typeof data.height === 'number'
    ) {
      frame.style.height = `${data.height}px`
    }
    // The form asks for this when it swaps in the (much taller) outcome screen,
    // which would otherwise render below the fold. Scrolling to the frame's top
    // is stable whether or not the height message has landed yet.
    if (data?.type === 'mr-lead-form-scroll') {
      scrollToFrame(frame, container.dataset.scrollOffset)
    }
  })
}

const init = (): void => {
  const containers = document.querySelectorAll<HTMLElement>(
    '[data-mr-lead-form-frame]'
  )
  for (const container of containers) {
    mount(container)
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
